/**
 * Calendar Mutation Hooks
 * Mutations for creating, updating, and deleting calendar events
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../useAuth";
import { apiPost, apiDelete, apiPut } from "../../../lib/api-utils";
import { useToast } from "../../use-toast";
import { ENDPOINTS, TOAST_MESSAGES, RETRY_CONFIG } from "../config";
import { queryKeys, getCalendarQueryKeysToInvalidate } from "../query-keys";
import { getErrorMessage } from "../utils";

// ============================================================================
// HELPER FUNCTION FOR COMMON MUTATION PATTERN
// ============================================================================

/**
 * Create a standardized calendar event mutation
 * Reduces code duplication for similar event operations
 */
function createCalendarEventMutation(
  endpoint: string,
  successMessage: { title: string; description: string },
  errorMessage: { title: string; description: string },
  invalidateKeys: ReturnType<typeof getCalendarQueryKeysToInvalidate>
) {
  return {
    onSuccess: () => {
      const { toast } = useToast();
      toast(successMessage);
      const queryClient = useQueryClient();
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error: any) => {
      const { toast } = useToast();
      const message = getErrorMessage(error, errorMessage.description);
      toast({
        title: errorMessage.title,
        description: message,
        variant: "destructive",
      });
    },
  };
}

// ============================================================================
// EVENT CREATION MUTATIONS
// ============================================================================

/**
 * Create Google Calendar Event
 */
export const useCreateGoogleEvent = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const invalidateKeys = getCalendarQueryKeysToInvalidate();

  return useMutation({
    mutationFn: async (eventData: any) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await apiPost(
        ENDPOINTS.events.createGoogle,
        eventData,
        token
      );

      return response;
    },
    retry: RETRY_CONFIG.standard,
    onSuccess: () => {
      toast(TOAST_MESSAGES.calendar.createSuccess);
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error: any) => {
      const message = getErrorMessage(
        error,
        TOAST_MESSAGES.calendar.createError.description
      );
      toast({
        title: TOAST_MESSAGES.calendar.createError.title,
        description: message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Create Microsoft Calendar Event
 */
export const useCreateMicrosoftEvent = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const invalidateKeys = getCalendarQueryKeysToInvalidate();

  return useMutation({
    mutationFn: async (eventData: any) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await apiPost(
        ENDPOINTS.events.createMicrosoft,
        eventData,
        token
      );

      return response;
    },
    retry: RETRY_CONFIG.standard,
    onSuccess: () => {
      toast(TOAST_MESSAGES.calendar.createSuccess);
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error: any) => {
      const message = getErrorMessage(
        error,
        TOAST_MESSAGES.calendar.createError.description
      );
      toast({
        title: TOAST_MESSAGES.calendar.createError.title,
        description: message,
        variant: "destructive",
      });
    },
  });
};

// ============================================================================
// EVENT DELETION MUTATIONS
// ============================================================================

/**
 * Delete Google Calendar Event
 */
export const useDeleteGoogleEvent = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const invalidateKeys = getCalendarQueryKeysToInvalidate();

  return useMutation({
    mutationFn: async (eventId: string) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await apiDelete(
        ENDPOINTS.calendar.google,
        token,
        { eventId }
      );

      return response;
    },
    retry: RETRY_CONFIG.standard,
    onSuccess: () => {
      toast(TOAST_MESSAGES.calendar.deleteSuccess);
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error: any) => {
      const message = getErrorMessage(
        error,
        TOAST_MESSAGES.calendar.deleteError.description
      );
      toast({
        title: TOAST_MESSAGES.calendar.deleteError.title,
        description: message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Delete Microsoft Calendar Event
 */
export const useDeleteMicrosoftEvent = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const invalidateKeys = getCalendarQueryKeysToInvalidate();

  return useMutation({
    mutationFn: async (eventId: string) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await apiDelete(
        ENDPOINTS.calendar.microsoft,
        token,
        { eventId }
      );

      return response;
    },
    retry: RETRY_CONFIG.standard,
    onSuccess: () => {
      toast(TOAST_MESSAGES.calendar.deleteSuccess);
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error: any) => {
      const message = getErrorMessage(
        error,
        TOAST_MESSAGES.calendar.deleteError.description
      );
      toast({
        title: TOAST_MESSAGES.calendar.deleteError.title,
        description: message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Delete Event (Auto-detects platform)
 * Uses the appropriate delete mutation based on event type
 */
export const useDeleteEvent = () => {
  const deleteGoogleEvent = useDeleteGoogleEvent();
  const deleteMicrosoftEvent = useDeleteMicrosoftEvent();

  return useMutation({
    mutationFn: async ({
      event,
      eventId,
    }: {
      event?: any;
      eventId?: string;
    }) => {
      const id = eventId || event?.id;
      if (!id) throw new Error("Event ID is required");

      // Determine platform based on event properties
      const isMicrosoft = event?.["@odata.etag"] || event?.odataEtag;

      if (isMicrosoft) {
        return deleteMicrosoftEvent.mutateAsync(id);
      } else {
        return deleteGoogleEvent.mutateAsync(id);
      }
    },
    retry: RETRY_CONFIG.standard,
    onSuccess: () => {
      // Success handling is done by individual mutations
    },
    onError: (error: any) => {
      // Error handling is done by individual mutations
      console.error("Delete event error:", error);
    },
  });
};

// ============================================================================
// EVENT UPDATE MUTATIONS
// ============================================================================

/**
 * Update Google Calendar Event
 */
export const useUpdateGoogleEvent = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const invalidateKeys = getCalendarQueryKeysToInvalidate();

  return useMutation({
    mutationFn: async ({
      eventId,
      eventDetails,
    }: {
      eventId: string;
      eventDetails: any;
    }) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await apiPut(
        ENDPOINTS.calendar.google,
        { eventId, eventDetails },
        token
      );

      return response;
    },
    retry: RETRY_CONFIG.standard,
    onSuccess: () => {
      toast(TOAST_MESSAGES.calendar.updateSuccess);
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error: any) => {
      const message = getErrorMessage(
        error,
        TOAST_MESSAGES.calendar.updateError.description
      );
      toast({
        title: TOAST_MESSAGES.calendar.updateError.title,
        description: message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Update Microsoft Calendar Event
 */
export const useUpdateMicrosoftEvent = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const invalidateKeys = getCalendarQueryKeysToInvalidate();

  return useMutation({
    mutationFn: async ({
      eventId,
      eventDetails,
    }: {
      eventId: string;
      eventDetails: any;
    }) => {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token");

      const response = await apiPut(
        ENDPOINTS.calendar.microsoft,
        { eventId, eventDetails },
        token
      );

      return response;
    },
    retry: RETRY_CONFIG.standard,
    onSuccess: () => {
      toast(TOAST_MESSAGES.calendar.updateSuccess);
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    },
    onError: (error: any) => {
      const message = getErrorMessage(
        error,
        TOAST_MESSAGES.calendar.updateError.description
      );
      toast({
        title: TOAST_MESSAGES.calendar.updateError.title,
        description: message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Update Event (Auto-detects platform)
 * Uses the appropriate update mutation based on event type
 */
export const useUpdateEvent = () => {
  const updateGoogleEvent = useUpdateGoogleEvent();
  const updateMicrosoftEvent = useUpdateMicrosoftEvent();

  return useMutation({
    mutationFn: async ({
      event,
      eventId,
      eventDetails,
    }: {
      event?: any;
      eventId?: string;
      eventDetails: any;
    }) => {
      const id = eventId || event?.id;
      if (!id) throw new Error("Event ID is required");

      // Determine platform based on event properties
      const isMicrosoft = event?.["@odata.etag"] || event?.odataEtag;

      if (isMicrosoft) {
        return updateMicrosoftEvent.mutateAsync({ eventId: id, eventDetails });
      } else {
        return updateGoogleEvent.mutateAsync({ eventId: id, eventDetails });
      }
    },
    retry: RETRY_CONFIG.standard,
    onSuccess: () => {
      // Success handling is done by individual mutations
    },
    onError: (error: any) => {
      // Error handling is done by individual mutations
      console.error("Update event error:", error);
    },
  });
};
